// @ts-strict-ignore
import React, { type ReactElement, useEffect, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend as Backend } from 'react-dnd-html5-backend';
import { useDispatch, useSelector } from 'react-redux';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useHref,
  useLocation,
} from 'react-router-dom';

import { usePostHog } from 'posthog-js/react';

import { addNotification } from 'loot-core/client/actions';
import { send } from 'loot-core/platform/client/fetch';
import { SpreadsheetProvider } from 'loot-core/src/client/SpreadsheetProvider';
import * as undo from 'loot-core/src/platform/client/undo';

import { useActions } from '../hooks/useActions';
import { useDemoSite } from '../hooks/useDemoSite';
import { useLocalPref } from '../hooks/useLocalPref';
import { useNavigate } from '../hooks/useNavigate';
import { useSync } from '../hooks/useSync';
import { useProgressTracking } from '../hooks/useProgressTracking';
import { useResponsive } from '../ResponsiveProvider';
import { theme } from '../style';
import { ExposeNavigate } from '../util/router-tools';

import { ChangePassword } from './auth/change-password/ChangePassword';
import { Login } from './auth/login/Login';
import { MyProfile } from './auth/my-profile/MyProfile';
import { Billing } from './billing/Billing';
import { BillingThankYou } from './BillingThankYou';
import { BudgetMonthCountProvider } from './budget/BudgetMonthCountContext';
import BudgetTemplatePage from './budget-template';
import ManageCategoriesPage from './categories/ManageCategoriesPage';
import { CountdownBanner } from './common/CountdownBanner';
import { Header } from './common/header/Header';
import { View } from './common/View';
import { ProgressChecklistProvider } from './onboarding/ProgressChecklistContext';
import { ProgressChecklist } from './onboarding/ProgressChecklist';
import { ProgressChecklistDemo } from './onboarding/ProgressChecklistDemo';
import Dashboard from './dashboard/Dashboard';
import { DemoBudgetDateLimit } from './DemoBudgetDateLimit';
import { GlobalKeys } from './GlobalKeys';
import { ManageRulesPage } from './ManageRulesPage';
import { Category } from './mobile/budget/Category';
import { MobileNavTabs } from './mobile/MobileNavTabs';
import { TransactionEdit } from './mobile/transactions/TransactionEdit';
import { Modals } from './Modals';
import { usePageTitle } from './PageTitleContext';
import { ManagePayeesPage } from './payees/ManagePayeesPage';
import { PostBudgetCreation } from './PostBudgetCreation';
import { Reports } from './reports';
import { NarrowAlternate, WideComponent } from './responsive';
import { ScrollProvider } from './ScrollProvider';
import { Settings } from './settings';
import { FloatableSidebar } from './sidebar';
import { SidebarProvider } from './sidebar/SidebarProvider';
import StorageWarning from './StorageWarning';
import { TitlebarProvider } from './Titlebar';

function NarrowNotSupported({
  redirectTo = '/budget',
  children,
}: {
  redirectTo?: string;
  children: ReactElement;
}) {
  const { isNarrowWidth } = useResponsive();
  const navigate = useNavigate();
  useEffect(() => {
    if (isNarrowWidth) {
      navigate(redirectTo);
    }
  }, [isNarrowWidth, navigate, redirectTo]);
  return isNarrowWidth ? null : children;
}

function WideNotSupported({ children, redirectTo = '/budget' }) {
  const { isNarrowWidth } = useResponsive();
  const navigate = useNavigate();
  useEffect(() => {
    if (!isNarrowWidth) {
      navigate(redirectTo);
    }
  }, [isNarrowWidth, navigate, redirectTo]);
  return isNarrowWidth ? children : null;
}

function RouterBehaviors() {
  const location = useLocation();
  const href = useHref(location);
  useEffect(() => {
    undo.setUndoState('url', href);
  }, [href]);

  return null;
}

function FinancesAppWithoutContext() {
  const actions = useActions();
  const dispatch = useDispatch();
  const billingStatus = useSelector(state => state.auth.billingStatus);
  const { pageTitle } = usePageTitle();
  const [cloudFileId] = useLocalPref('cloudFileId');
  const { isDemoBudgetFile } = useDemoSite();
  
  // Track progress for onboarding checklist
  useProgressTracking();

  useEffect(() => {
    async function run() {
      if (!billingStatus.isActive) {
        return;
      }

      if (cloudFileId) {
        const files = await actions.loadAllFiles();
        const cloudFile = files.find(o => o['cloudFileId'] === cloudFileId);
        if (cloudFile.state === 'synced') {
          await send('set-syncing-mode', { mode: 'enabled' });
        } else {
          await send('set-syncing-mode', { mode: 'offline' });
        }
      } else {
        if (billingStatus.isActive) {
          console.info('Registering a file to cloud');
          await actions.uploadBudget();
          // Wait a little bit to make sure the sync button will get the
          // sync start event. This can be improved later.
          setTimeout(async () => {
            await actions.sync();
          }, 100);
          actions.loadPrefs();

          await send('set-syncing-mode', { mode: 'enabled' });
          dispatch(
            addNotification({
              type: 'message',
              message:
                'Your budget file is now enabled for device synchronization.',
            }),
          );
        }
      }
    }
    run();
  }, [billingStatus?.isActive]);

  return (
    <BrowserRouter>
      <RouterBehaviors />
      <ExposeNavigate />
      <DemoBudgetDateLimit />
      {/*<MobileUnderConstruction />*/}
      <BillingThankYou />
      <PostBudgetCreation />
      <View style={{ height: '100%', minHeight: 'auto' }}>
        <GlobalKeys />

        <View
          style={{
            flexDirection: 'row',
            backgroundColor: theme.pageBackground,
            flex: 1,
          }}
        >
          <FloatableSidebar />

          <View
            style={{
              color: theme.pageText,
              backgroundColor: theme.pageBackground,
              flex: 1,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <Header pageTitle={pageTitle} />
            <CountdownBanner />
            <div
              style={{
                flex: 1,
                display: 'flex',
                overflow: 'auto',
                position: 'relative',
              }}
            >
              <Routes>
                <Route path="/" element={<Dashboard />} />

                <Route path="/login" element={<Login />} />

                <Route path="/my-profile" element={<MyProfile />} />

                <Route path="/change-password" element={<ChangePassword />} />

                <Route path="/reports/*" element={<Reports />} />

                {!isDemoBudgetFile && (
                  <Route path="/billing" element={<Billing inManagementApp={false} />} />
                )}

                <Route
                  path="/planning"
                  element={
                    <NarrowNotSupported>
                      <BudgetTemplatePage />
                    </NarrowNotSupported>
                  }
                />

                <Route
                  path="/budget"
                  element={<NarrowAlternate name="Budget" />}
                />

                <Route
                  path="/schedules"
                  element={
                    <NarrowNotSupported>
                      <WideComponent name="Schedules" />
                    </NarrowNotSupported>
                  }
                />

                <Route path="/categories" element={<ManageCategoriesPage />} />
                <Route path="/payees" element={<ManagePayeesPage />} />
                <Route path="/rules" element={<ManageRulesPage />} />
                <Route path="/settings" element={<Settings />} />

                <Route
                  path="/gocardless/link"
                  element={
                    <NarrowNotSupported>
                      <WideComponent name="GoCardlessLink" />
                    </NarrowNotSupported>
                  }
                />

                <Route
                  path="/accounts"
                  element={<NarrowAlternate name="Accounts" />}
                />

                <Route
                  path="/accounts/:id"
                  element={<NarrowAlternate name="Account" />}
                />

                <Route
                  path="/transactions/:transactionId"
                  element={
                    <WideNotSupported>
                      <TransactionEdit />
                    </WideNotSupported>
                  }
                />

                <Route
                  path="/categories/:id"
                  element={
                    <WideNotSupported>
                      <Category />
                    </WideNotSupported>
                  }
                />

                {/* redirect all other traffic to the dashboard page */}
                <Route path="/*" element={<Navigate to="/" replace />} />
              </Routes>

              <Modals />
              <ProgressChecklist />
            </div>

            <Routes>
              <Route path="*" element={<MobileNavTabs />} />
              <Route path="/reports" element={<MobileNavTabs />} />
              <Route path="/reports/*" element={null} />
              <Route path="/accounts" element={<MobileNavTabs />} />
              <Route path="/accounts/*" element={null} />
              <Route path="/transactions/*" element={null} />
              <Route path="/categories" element={<MobileNavTabs />} />
            </Routes>
          </View>
        </View>
      </View>
    </BrowserRouter>
  );
}

export function FinancesApp() {
  const app = useMemo(() => <FinancesAppWithoutContext />, []);
  const { smartSync } = useSync();
  const userData = useSelector(state => state.user.data);

  const posthog = usePostHog();

  useEffect(() => {
    smartSync();
  }, []);

  useEffect(() => {
    (async () => {
      posthog?.identify(userData.name, {
        email: userData.email,
      });
    })();
  }, [posthog]);

  return (
    <SpreadsheetProvider>
      <TitlebarProvider>
        <SidebarProvider>
          <BudgetMonthCountProvider>
            <ProgressChecklistProvider>
              <StorageWarning />
              <DndProvider backend={Backend}>
                <ScrollProvider>{app}</ScrollProvider>
              </DndProvider>
            </ProgressChecklistProvider>
          </BudgetMonthCountProvider>
        </SidebarProvider>
      </TitlebarProvider>
    </SpreadsheetProvider>
  );
}
